import { runInAction } from 'mobx';
import { Instance, types } from 'mobx-state-tree';
import { transl } from '../main/transl';
import { mg } from '../mg/mg';
import { IMgColumn, MgColumn } from '../mg/table/MgColumn';
import { Momentino } from '../services/Momentino';
import { getOmnistore } from './Omnistore';

const simpleTypes = ['bool', 'int', 'string', 'date', 'time', 'datetime', 'object', 'price'];
const specialTypes = ['multiselect', 'amountToOrder', 'pricePair', 'dateAndTime', 'nameSearch', 'addToCart', 'harmoStatus', 'harmoPrimarySecondary', 'harmoInfo'];
const unsortableTypes = ['amountToOrder', 'addToCart', 'harmoPrimarySecondary'];

export const DataviewStoreColumnHider = types.enumeration('ColumnHider',
	['DEFAULT', 'HIDDEN', 'PRICE', 'FIXED', 'CATALOG', 'NON_CATALOG', 'NON_LTEMPLATES']);

export const DataviewStoreColumn = types
	.model('DataviewStore', {
		name: types.string,
		fieldName: types.string,
		widthDesc: types.number,
		type: types.string,
		isEnum: types.boolean,
		isSpecial: types.boolean,
		isRequired: types.boolean,
		_line: types.string,
		_columnHiders: types.array(DataviewStoreColumnHider),
	})
	.views(self => ({
		hasColumnHider(reason) {
			return self._columnHiders.indexOf(reason) > -1;
		},
	}))
	.actions(self => ({
		addColumnHider(reason: Instance<typeof DataviewStoreColumnHider>) {
			const a = self._columnHiders;
			if (a.indexOf(reason) < 0) a.push(reason);
		},
		removeColumnHider(reason: Instance<typeof DataviewStoreColumnHider>) {
			const a = self._columnHiders;
			const i = a.indexOf(reason);
			if (i > -1) a.splice(i, 1);
		},
	}))
	.actions(self => ({
		toggleColumnHider(reason) {
			self.hasColumnHider(reason) ? self.removeColumnHider(reason) : self.addColumnHider(reason);
		},
	}))
	.views(self => ({
		get isSortable() {
			return !unsortableTypes.includes(self.type);
		},
		isVisibleIgnoring(hiders) {
			for (let r of self._columnHiders) {
				if (hiders.indexOf(r) < 0) return false;
			}
			return true;
		},
	}))
	.views(self => ({
		get emptyValue() {
			switch (self.type) {
			case 'string': return '';
			case 'int': return 0;
			case 'bool': return false;
			case 'multiselect': return false;
			case 'amountToOrder': return 0;
			case 'price': return 0;
			}
			return '';
		},
	}))
	.views(self => ({
		sanitize(value, entity) {
			if (mg.isNullOrUndefined(value)) return self.emptyValue;
			switch (self.type) {
			case 'string': return ('' + value).trim();
			case 'int': return value>0 ? +value : 0;
			case 'amountToOrder': {
				let n = +value;
				if (isNaN(n) || n<=0) return 0;
				n = Math.round(n);
				const amountStep = +(entity.amountStep || 1);
				if (amountStep === 1) return n;
				return amountStep * Math.round(n / amountStep);
			}
			}
			return value;
		},
	}))
	.views(self => ({
		accessors(entity: {[s: string]: string | number | boolean}) {
			const dotIndex = self.fieldName.indexOf('.');
			return {
				name: self.name,
				getter() : any {
					if (dotIndex===-1) return entity[self.fieldName];
					const multirepo = getOmnistore().multirepo;
					return multirepo.externalGet(entity, self.fieldName, dotIndex);
				},
				setter(value: string | number | boolean) {
					entity[self.fieldName] = value;
				},
				handleChange(event: {target: {value: string | number | boolean, type?: string}}) {
					const t = event.target;
					console.log('t', t);
					let v = t.type==='checkbox' ? !entity[self.fieldName] : self.sanitize(t.value, entity);
					if (self.type === 'object') v = JSON.parse(v);
					runInAction(() => entity[self.fieldName] = v);
				},
			};
		},
	}))
	.views(self => ({
		formatForSort(entity) {
			const {isEnum, type} = self;
			const value = self.accessors(entity).getter();
			if (isEnum) return transl(value);
			switch (type) {
			case 'pricePair': {
				const {retailPrice, listPrice} = entity;
				return retailPrice>0 ? +retailPrice : listPrice>0 ? + listPrice : 0;
			}
			case 'dateAndTime': {
				const d = entity.date;
				const t = entity.time;
				return !d ? '' : !t ? d : d + ' ' + t;
			}
			case 'int': return +value;
			case 'price': return value>0 || value<0 ? value.toFixed(3) : 0;
			case 'harmoPrimarySecondary':
			case 'harmoStatus':
			{
				if (typeof value !== 'string') return value;
				const i = value.indexOf('/');
				return +value.substring(0, i);
			}
			case 'harmoInfo': {
				return entity.harmonization?.timestamp ?? '';
			}
			}
			if (mg.isNullOrUndefined(value)) return self.emptyValue;
			return value;
		},
	}))
	.views(self => ({
		comparator(reversed: boolean) {
			const r = reversed ? -1 : +1;
			return function(a, b) {
				let ax = self.formatForSort(a);
				let bx = self.formatForSort(b);
				if (typeof ax !== typeof bx) {
					ax += '';
					bx += '';
				}
				if (typeof ax === 'string') {
					ax = ax.toLowerCase();
					bx = bx.toLowerCase();
				}
				return ax<bx ? -r : ax>bx ? +r : 0;
			};
		},
		formatForView(entity) : string {
			const {fieldName, isEnum, type} = self;
			let value = self.accessors(entity).getter();
			const isMissing = mg.isNullOrUndefined(value);
			if (isEnum) return isMissing ? '' : transl(type + '.' + value);

			if (typeof value === 'string') {
				if (value.length === 19) {
					switch (type) {
					case 'date': return Momentino.timestampTo.date(value);
					case 'time': return Momentino.timestampTo.time(value);
					case 'datetime': return Momentino.timestampTo.datetime(value);
					}
				} else if (value.length === 10) {
					switch (type) {
					case 'date': return Momentino.dateTo.date(value);
					}
				}
			}
			switch (type) {
			case 'bool': return transl(value ? 'yes' : 'no');
			case 'int': return +value!==0 ? '' + +value : '0';
			case 'price': return value>0 || value<0 ? value.toFixed(3).replace('.', ',') : '';
			case 'dateAndTime': return self.formatForSort(entity);
			default: return isMissing ? '' : value;
			}
		},
		preclean(value) {
			if (mg.isNullOrUndefined(value)) return self.emptyValue;
			switch (self.type) {
			case 'amountToOrder': return value>=0 ? +value : mg.isString(value) ? +value.replace(/\D/g, '') : 0;
			}
			return value;
		},
	}));

export type IDataviewStoreColumn = Instance<typeof DataviewStoreColumn>

const adaptTypeMap = {
	nonNegInt: 'int',
	posInt: 'int',
	color: 'string',
	imageLink: 'string',
};

export function newDataviewStoreColumn(line: string) : IDataviewStoreColumn {
	const list = line.split(' ');
	let name = list.shift();
	if (!mg.checkPresent(name, 'missing name', line)) throw new Error();
	if (name?.startsWith('/')) name = name.substring(1);
	const isRequired = name[0] === '*';
	if (isRequired) name = name.substring(1);
	if (!mg.checkPresent(name, 'missing name', line)) throw new Error();
	const fieldName = name.replace(/:.*/, '');
	let type = name.substring(fieldName.length + 1);
	let isEnum = false;
	let widthDesc = 0;
	let isSpecial = false;
	list.forEach(s => {
		if (s.startsWith('=')) return; //TODO
		const m = s.match(/^(?:(\d+)|:([A-Za-z]\w+))$/);
		if (!m) throw new Error('Illegal column: «' + s + '» in «' + line + '»');
		if (m[1]) {
			widthDesc = +m[1];
		} else if (m[2]) {
			type = m[2];
			type = adaptTypeMap[type] ?? type;
			isSpecial = false;
			isEnum = false;
			if (simpleTypes.indexOf(type) > -1) {
				// nothing to do
			} else if (type[0].match(/^[A-Z]/)) {
				isEnum = true;
			} else if (specialTypes.indexOf(type) > -1) {
				isSpecial = true;
			} else {
				throw new Error('Illegal column type: «' + s + '» in «' + line + '»');
			}
		} else {
			throw new Error('Error parsing column: ' + s);
		}
	});
	if (!widthDesc) widthDesc = type==='bool' || type==='multiselect' ? 40 : 100;
	return DataviewStoreColumn.create({name, fieldName, widthDesc, type, isEnum, isSpecial, isRequired, _line: line});
}

export function adaptDataviewStoreColumn(column: Instance<typeof DataviewStoreColumn>) : IMgColumn {
	let {name, fieldName, type, isRequired, emptyValue, sanitize, accessors} = column;
	if (type === 'bool') {
		type = 'boolean';
	} else if (type === '') {
		type = 'string';
	}
	return MgColumn._BASE.with({name, fieldName, isRequired, type, emptyValue, sanitize, accessors});
}
